import React, {FC, useRef} from 'react';
import {ModerationUser} from "interfaces";
import {Accordion} from "react-bootstrap";
import {ModerationTag} from "./ModerationTag";
import {AutoSizer, CellMeasurer, CellMeasurerCache, List} from "react-virtualized";


interface Props {
    user: ModerationUser
}

export const ModerationTags: FC<Props> = ({user}) => {
    const hasReviewTags = !!user.reviewTags?.length;
    const newTags = hasReviewTags ? user.reviewTags.filter(t => !user.reviewedDate || (t.date && t.date > user.reviewedDate)) : [];
    const oldTags = hasReviewTags ? user.reviewTags.filter(t => (!newTags.find(nt => t.tag === nt.tag && t.date === nt.date))) : [];

    const totalNew = newTags.length;
    const totalOld = oldTags.length;

    const getListHeight = (count: number) => {
        if (!count) {
            return 0;
        }

        const estimatedRowHeight = 90; // approximate tag row height including padding/margins
        const maxHeight = 600;

        return Math.max(170, Math.min(maxHeight, count * estimatedRowHeight));
    };

    const newTagsCacheRef = useRef(
        new CellMeasurerCache({
            defaultHeight: 72,
            fixedWidth: true
        })
    );

    const oldTagsCacheRef = useRef(
        new CellMeasurerCache({
            defaultHeight: 72,
            fixedWidth: true
        })
    );


    if (!totalNew && !totalOld) {
        return null;
    }


    const renderNewRow = ({index, key, parent, style}: any) => {
        const tag = newTags[index];
        return (
            <CellMeasurer
                key={key}
                cache={newTagsCacheRef.current}
                columnIndex={0}
                rowIndex={index}
                parent={parent}
            >
                <div style={style}>
                    <ModerationTag
                        key={tag.tag + tag.date?.toString() + user.id + 'new' + index}
                        user={user}
                        tag={tag}
                    />
                </div>
            </CellMeasurer>
        );
    };

    const renderOldRow = ({index, key, parent, style}: any) => {
        const tag = oldTags[index];
        return (
            <CellMeasurer
                key={key}
                cache={oldTagsCacheRef.current}
                columnIndex={0}
                rowIndex={index}
                parent={parent}
            >
                <div style={style}>
                    <ModerationTag
                        key={tag.tag + tag.date?.toString() + user.id + 'old' + index}
                        user={user}
                        tag={tag}
                    />
                </div>
            </CellMeasurer>
        );
    };

    return <div>
        <hr style={{opacity: 0.1}}/>
        <Accordion key={user.id} defaultActiveKey="0" className="mt-2" flush alwaysOpen>
            {!!newTags.length &&
                <Accordion.Item eventKey="0">
                    <Accordion.Header>Непросмотренная история изменений ({newTags.length})</Accordion.Header>
                    <Accordion.Body>
                        <div className="container" style={{height: getListHeight(newTags.length)}}>
                            <AutoSizer>
                                {({height, width}) => (
                                    <List
                                        width={width}
                                        height={height}
                                        rowCount={totalNew}
                                        deferredMeasurementCache={newTagsCacheRef.current}
                                        rowHeight={newTagsCacheRef.current.rowHeight}
                                        rowRenderer={renderNewRow}
                                        overscanRowCount={10}
                                    />
                                )}
                            </AutoSizer>
                        </div>
                    </Accordion.Body>
                </Accordion.Item>}
            {!!oldTags.length &&
                <Accordion.Item eventKey="1">
                    <Accordion.Header>Просмотренная история изменений ({oldTags.length})</Accordion.Header>
                    <Accordion.Body>
                        <div className="container" style={{height: getListHeight(oldTags.length)}}>
                            <AutoSizer>
                                {({height, width}) => (
                                    <List
                                        width={width}
                                        height={height}
                                        rowCount={totalOld}
                                        deferredMeasurementCache={oldTagsCacheRef.current}
                                        rowHeight={oldTagsCacheRef.current.rowHeight}
                                        rowRenderer={renderOldRow}
                                        overscanRowCount={10}
                                    />
                                )}
                            </AutoSizer>
                        </div>
                    </Accordion.Body>
                </Accordion.Item>
            }
        </Accordion>
    </div>
}
